import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Box, Text, Hidden, ResponsiveRender } from '@machobear/design-system'
import AreaTitle from '@/components/area-title'
import SearchBar from '@/components/search-bar'
import { Button } from '@/components/button'
import { Label } from '@/components/text'
import { Stack } from '@/components/structure'
import { Tour } from '@/components/tour'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints, structure } from '@/styles'
import MobileFooterButton from '@/components/mobile-footer-button'
import useDelayedMount from '@/hooks/use-delayed-mount'
import { useHighlight } from '@/models/toaster/hooks'

const PageHeader = styled.div`
  top: ${structure.header.height}px;
  position: sticky;
  z-index: 21;
  background-color: ${colors.white};
  display: flex;
  justify-content: center;

  @media only screen and (max-width: ${breakpoints.largeDesktop}) {
    justify-content: flex-start;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: ${structure.header.heightMobile}px;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    box-shadow: 0 2px 8px 0 rgba(146, 146, 146, 0.69);
  }
`

const PageHeaderInner = styled.div`
  max-width: 900px;
  margin: 0 50px;
  padding-bottom: 40px;
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  position: relative;

  @media only screen and (max-width: ${breakpoints.largeDesktop}) {
    margin: 0 30px;
    min-width: 620px;
    flex-direction: column;
    align-items: flex-start;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 0 25px;
    padding: 20px 0;
    min-width: unset;
    height: 80px;
    justify-content: center;

    > div {
      width: 100%;
    }
  }
`

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 50px;

  @media only screen and (max-width: ${breakpoints.largeDesktop}) {
    padding-top: 40px;
    justify-content: flex-start;
    width: 100%;
  }

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    padding-top: 0px;
  }
`

const SearchInner = styled.div`
  display: flex;
  align-items: center;
  gap: 0 24px;
  > button {
    white-space: nowrap;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const ManageUsersHeader = ({ addUser, onSearch, disableAdd }) => {
  const { t } = useTranslation()
  const [searchString, setSearchString] = useState('')
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const pageTitle = t('l.profile.list.manage_users.title', 'Manage Users')
  return (
    <PageHeader>
      <PageHeaderInner>
        <Hidden below="tablet">
          <AreaTitle textAlign="left" top="40" bottom="10">
            {pageTitle}
          </AreaTitle>
          <Box paddingRight="xxsmall">
            <Text tone="secondary" size="small">
              {t(
                'b.settings.view.manage.users.title',
                'Keep your team organized and assign specific listings to individual users.'
              )}
            </Text>
          </Box>
        </Hidden>
        <FiltersWrapper>
          <SearchInner>
            <Highlight>
              <ResponsiveRender above="mobile">
                <Button
                  className="add-new-user-btn"
                  theme="secondary"
                  onClick={addUser}
                  size="small"
                  disabled={disableAdd}
                >
                  {t('l.profile.list.add_new_user.button', 'Add new user')}
                </Button>
              </ResponsiveRender>

              <MobileFooterButton
                className="add-new-user-btn"
                onClick={addUser}
                disabled={disableAdd}
                displayBelow="tablet"
              >
                {t('l.profile.list.add_new_user.button', 'Add new user')}
              </MobileFooterButton>
            </Highlight>
            <SearchBar
              placeholder={t('common.search', 'Search')}
              onSearch={onSearch}
              fullWidth={displayTablet}
              searchString={searchString}
              setSearchString={setSearchString}
            />
          </SearchInner>
        </FiltersWrapper>
      </PageHeaderInner>
    </PageHeader>
  )
}

const Highlight = ({ children }) => {
  const { hide, next: onBeforeClose } = useHighlight('invite_team.invite_team')
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const { t } = useTranslation()
  const delay = useDelayedMount(1000)
  const steps = useMemo(
    () => [
      {
        selector: '.add-new-user-btn',
        content: ({ next }) => (
          <Stack gutter={16} maxWidth={216}>
            <Stack gutter={4}>
              <Label fontSize="14px">
                {t('b.toaster.view.invite_team.tour.title', 'Invite your team')}
              </Label>
              <Box>
                <Label fontSize="12px" type="small">
                  {t(
                    'b.toaster.view.manage_users.tour.description',
                    'Add a new user to help manage your listings by providing their name, email address, phone number and role.'
                  )}
                </Label>
              </Box>
            </Stack>
            <Box width="full" display="flex">
              <Button size="small" onClick={next}>
                {t('b.toaster.view.key_features.tour.button', 'Got it!')}
              </Button>
            </Box>
          </Stack>
        )
      }
    ],
    [t]
  )
  return (
    <Tour
      hide={hide || !delay}
      steps={steps}
      position={displayMobile ? 'top' : 'right'}
      positionStrategy={displayMobile ? 'none' : 'lt'}
      showCloseButton={false}
      onBeforeClose={onBeforeClose}
      allowInteraction
    >
      {children}
    </Tour>
  )
}

export default ManageUsersHeader
